// Header.js
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import logo from '../assets/logo.png';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCountriesOpen, setIsCountriesOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const timeoutRef = useRef(null);
  const servicesTimeoutRef = useRef(null);

  const countries = [
    {
      name: 'United States',
      slug: 'united-states',
      description: 'Employment-based categories (H-1B alternatives, L-1, O-1), family routes, adjustment of status, and consular processing.'
    },
    {
      name: 'Canada',
      slug: 'canada',
      description: 'Work permits (LMIA/exempt), Express Entry strategies, study permits, and PNP options.'
    },
    {
      name: 'United Kingdom',
      slug: 'united-kingdom',
      description: 'Skilled Worker & license guidance for sponsors, Graduate route, family and settlement.'
    },
    {
      name: 'Germany/EU',
      slug: 'germany-eu',
      description: 'EU Blue Card, local work permits, posted-worker notifications, and Schengen business travel.'
    },
    {
      name: 'Australia',
      slug: 'australia',
      description: 'Skilled visas, TSS/ENS, GTE-compliant student pathways, and partner visas.'
    },
    {
      name: 'UAE',
      slug: 'uae',
      description: 'Employer sponsorship, free zone processing, and family sponsorship.'
    }
  ];

  const services = [
    {
      name: 'Skilled Worker / Employment Visas',
      slug: 'skilled-worker-employment',
      description: 'Role mapping, eligibility, and end-to-end processing.'
    },
    {
      name: 'Study Permits & Graduate Pathways',
      slug: 'study-permits-graduate-pathways',
      description: 'Admissions coordination, GTE/financials, and post-study work routes.'
    },
    {
      name: 'Family & Dependent Visas',
      slug: 'family-and-dependent-visas',
      description: 'Spouse/partner, children, and reunification options.'
    },
    {
      name: 'Permanent Residence & Citizenship',
      slug: 'permanent-residence-and-citizenship',
      description: 'Long-term planning, points-based programs, and naturalisation support.'
    },
    {
      name: 'Investor & Entrepreneur Routes',
      slug: 'investor-and-entrepreneur-routes',
      description: 'Business plans, source-of-funds guidance, and filings.'
    },
    {
      name: 'Visitor & Short-Stay',
      slug: 'visitor-and-short-stay',
      description: 'Purpose-fit visitor guidance (meetings, conferences, training).'
    },
    {
      name: 'Appeals & Complex Cases',
      slug: 'appeals-and-complex-cases',
      description: 'Strategy and coordination with local counsel where attorney representation is required.'
    }
  ];

  const handleCountriesMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsCountriesOpen(true);
  };

  const handleCountriesMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsCountriesOpen(false);
    }, 150);
  };

  const handleServicesMouseEnter = () => {
    if (servicesTimeoutRef.current) {
      clearTimeout(servicesTimeoutRef.current);
    }
    setIsServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
    }, 150);
  };

  return (
    <header className="px-4 md:px-24 center w-full px-8 py-4 bg-white flex items-center justify-between relative z-50">
      <div className="flex items-center">
        <Link to="/">
        <img src={logo} alt="Alveria Claims Logo" className="h-10" />
        </Link>
      </div>
      
      {/* Desktop Nav */}
      <nav className="hidden md:flex space-x-8 text-acBlack text-lg font-nunitoSans">
        {/* Services Dropdown */}
        <div 
          className="relative"
          onMouseEnter={handleServicesMouseEnter}
          onMouseLeave={handleServicesMouseLeave}
        >
          <button className="flex items-center hover:opacity-80 transition">
            Services
            <ChevronDown size={16} className="ml-1" />
          </button>
          
          {isServicesOpen && (
            <div className="absolute top-full left-0 pt-1 z-50">
              <div className="w-80 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                {services.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/services/${service.slug}`}
                    className="block px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900 mb-1">{service.name}</div>
                    <div className="text-xs text-gray-600 leading-relaxed font-light">{service.description}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Countries Dropdown */}
        <div 
          className="relative"
          onMouseEnter={handleCountriesMouseEnter}
          onMouseLeave={handleCountriesMouseLeave}
        >
          <button className="flex items-center hover:opacity-80 transition">
            Countries
            <ChevronDown size={16} className="ml-1" />
          </button>
          
          {isCountriesOpen && (
            <div className="absolute top-full left-0 pt-1 z-50">
              <div className="w-80 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                {countries.map((country) => (
                  <Link
                    key={country.slug}
                    to={`/countries/${country.slug}`}
                    className="block px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900 mb-1">{country.name}</div>
                    <div className="text-xs text-gray-600 leading-relaxed font-light">{country.description}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <Link to="/about" className="hover:opacity-80 transition">About us</Link>
        <Link to="/contact" className="hover:opacity-80 transition">Contact</Link>
      </nav>
      
      {/* Mobile Menu Button */}
      <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <X size={30} /> : <Menu size={30} />}
      </button>
      
      {/* Mobile Nav */}
      {isOpen && (
        <div className="absolute top-16 left-0 w-full bg-white shadow-md flex flex-col items-center space-y-6 py-6 md:hidden z-40">
          {/* Mobile Services Section */}
          <div className="w-full px-4">
            <button 
              className="w-full text-center hover:opacity-80 transition mb-2"
              onClick={() => setIsServicesOpen(!isServicesOpen)}
            >
              Services
            </button>
            {isServicesOpen && (
              <div className="bg-gray-50 rounded-lg p-2 space-y-2 mb-4">
                {services.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/services/${service.slug}`}
                    className="block px-3 py-2 text-sm hover:bg-white rounded transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="font-medium text-gray-900">{service.name}</div>
                    <div className="text-xs text-gray-600 mt-1 font-light">{service.description}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          
          {/* Mobile Countries Section */}
          <div className="w-full px-4">
            <button 
              className="w-full text-center hover:opacity-80 transition mb-2"
              onClick={() => setIsCountriesOpen(!isCountriesOpen)}
            >
              Countries
            </button>
            {isCountriesOpen && (
              <div className="bg-gray-50 rounded-lg p-2 space-y-2">
                {countries.map((country) => (
                  <Link
                    key={country.slug}
                    to={`/countries/${country.slug}`}
                    className="block px-3 py-2 text-sm hover:bg-white rounded transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="font-medium text-gray-900">{country.name}</div>
                    <div className="text-xs text-gray-600 mt-1 font-light">{country.description}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          
          <Link to="/about" className="hover:opacity-80 transition" onClick={() => setIsOpen(false)}>About us</Link>
          <Link to="/contact" className="hover:opacity-80 transition" onClick={() => setIsOpen(false)}>Contact</Link>
        </div>
      )}
    </header>
  );
};

export default Header;
